import { useState } from "react";
import "../App.css";
import iconCaret from "../photos/caret.svg";
import iconPlus from "../photos/plus.svg";
import iconMinus from "../photos/minus.svg";
import { ListItem } from "./ListItem/ListItem";
import { DISPLAY_ORDER } from "./Recommendation";

interface PublicPlayerData {
  sessionId: string;
  name: string;
}

interface LobbyAccordionsProps {
  t: (key: string, options?: any) => any;
  containerHeight: number;
  setRoomInput: (value: string) => void;
  players: PublicPlayerData[];
  sessionId: string;
  isOwner: boolean;
  onAddName: (name: string) => void;
  onUpdateName: (name: string) => void;
  onKick: (targetSessionId: string) => void;
}

type OpenPanel = "tips" | "players" | null;

const NAME_MAX_LENGTH = 20;

const inputStyle = {
  flex: 1,
  minWidth: 0,
  padding: 0,
  border: "none",
  background: "transparent",
  boxShadow: "none",
  color: "inherit",
};

// Two stacked accordions, each laid out like Recommendation.js: "Forslag" with
// its panel first, then "Spillere (x)" with its panel. Only one panel is open
// at a time so the AI lobby looks like the classic lobby.
function LobbyAccordions({
  t,
  containerHeight,
  setRoomInput,
  players,
  sessionId,
  isOwner,
  onAddName,
  onUpdateName,
  onKick,
}: LobbyAccordionsProps) {
  const [open, setOpen] = useState<OpenPanel>(null);
  const [nameInput, setNameInput] = useState("");
  const [editing, setEditing] = useState(false);
  const [editValue, setEditValue] = useState("");

  const recs: { preview: string; text: string }[] =
    t("rec", { returnObjects: true }) || [];

  const toggle = (panel: Exclude<OpenPanel, null>) =>
    setOpen(open === panel ? null : panel);

  const commitName = () => {
    const name = nameInput.trim();
    if (!name) return;
    onAddName(name);
    setNameInput("");
  };

  const startEdit = (currentName: string) => {
    setEditValue(currentName);
    setEditing(true);
  };

  const saveEdit = () => {
    const name = editValue.trim();
    if (name) onUpdateName(name);
    setEditing(false);
  };

  const caret = (isOpen: boolean) => (
    <img
      style={{
        paddingLeft: "16px",
        paddingRight: "16px",
        rotate: "180deg",
      }}
      src={iconCaret}
      alt=""
      className={isOpen ? "rotate top-margin-s" : "rotate-back top-margin-s"}
    />
  );

  const header = (panel: Exclude<OpenPanel, null>, label: string) => (
    <div className="container-row top-margin-m" style={{ alignItems: "center" }}>
      <div className="container-row-start " onClick={() => toggle(panel)}>
        <div className="text-h2">{label}</div>
        {caret(open === panel)}
      </div>
    </div>
  );

  const panelStyle = {
    height: containerHeight - 500,
    overflow: "hidden",
    overflowY: "scroll" as const,
  };

  const playerRow = (player: PublicPlayerData) => {
    const isSelf = player.sessionId === sessionId;

    if (isSelf && editing) {
      return (
        <div
          key={player.sessionId}
          className="list-item-container divider"
          style={{ cursor: "default", alignItems: "center" }}
        >
          <input
            className="text-h4"
            value={editValue}
            onChange={(e) => setEditValue(e.currentTarget.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                saveEdit();
              }
            }}
            maxLength={NAME_MAX_LENGTH}
            autoComplete="off"
            autoFocus
            style={inputStyle}
          />
          <div
            className="text-h4"
            style={{ cursor: "pointer", whiteSpace: "nowrap" }}
            onClick={saveEdit}
          >
            {t("ai.players.save")}
          </div>
        </div>
      );
    }

    return (
      <div
        key={player.sessionId}
        className="list-item-container divider"
        style={{ cursor: "default", alignItems: "center" }}
      >
        <div className="text-h4">{player.name}</div>
        {isSelf && (
          <div
            className="text-h4"
            style={{ cursor: "pointer", whiteSpace: "nowrap" }}
            onClick={() => startEdit(player.name)}
          >
            {t("ai.players.edit")}
          </div>
        )}
        {!isSelf && isOwner && (
          <img
            style={{ paddingTop: "3px", cursor: "pointer" }}
            src={iconMinus}
            alt={t("ai.players.kick")}
            onClick={() => onKick(player.sessionId)}
          />
        )}
      </div>
    );
  };

  return (
    <div style={{ marginBottom: "10px" }}>
      {header("tips", t("tips"))}
      {open === "tips" && (
        <div style={panelStyle} className="recommendation-slide-in">
          {DISPLAY_ORDER.map((idx) => {
            const rec = recs[idx];
            if (!rec) return null;
            return (
              <ListItem
                key={idx}
                recPreview={rec.preview}
                recText={rec.text}
                setRoomInput={setRoomInput}
              />
            );
          })}
        </div>
      )}

      {header("players", t("ai.players.title", { count: players.length }))}
      {open === "players" && (
        <div style={panelStyle} className="recommendation-slide-in">
          {players.map(playerRow)}
          {isOwner && (
            <div
              className="list-item-container divider"
              style={{ cursor: "default", alignItems: "center" }}
            >
              <input
                className="text-h4"
                value={nameInput}
                onChange={(e) => setNameInput(e.currentTarget.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    commitName();
                  }
                }}
                onBlur={commitName}
                placeholder={t("ai.nameEntry.placeholder")}
                maxLength={NAME_MAX_LENGTH}
                autoComplete="off"
                style={inputStyle}
              />
              <img
                style={{ paddingTop: "3px", cursor: "pointer" }}
                src={iconPlus}
                alt=""
                onClick={commitName}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default LobbyAccordions;
